import { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import ManageBookingModal from '../modals/ManageBookingModal';
import Toast from '../components/Toast';

// PublicPortal — customer-facing self-service page.
// Mounted by App.jsx when the URL matches /manage/BK-XXXX or ?manage=BK-XXXX
// (instead of the operator CRM AppShell).
//
// Reuses the same AppProvider context as the CRM so it sees the real bookings
// stored in localStorage. After email-match auth succeeds, it opens the existing
// ManageBookingModal which already handles view / cancel / date-change.

const MAX_TRIES_PER_WINDOW = 5;
const RATE_LIMIT_WINDOW_MS = 10 * 60 * 1000;   // 10 minutes
const SS_KEY = 'fr_portal_tries';

function recordAttempt() {
  try {
    const now = Date.now();
    const raw = sessionStorage.getItem(SS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    const recent = arr.filter(t => now - t < RATE_LIMIT_WINDOW_MS);
    recent.push(now);
    sessionStorage.setItem(SS_KEY, JSON.stringify(recent));
    return recent.length;
  } catch { return 1; }
}

function tooManyAttempts() {
  try {
    const now = Date.now();
    const raw = sessionStorage.getItem(SS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    const recent = arr.filter(t => now - t < RATE_LIMIT_WINDOW_MS);
    return recent.length >= MAX_TRIES_PER_WINDOW;
  } catch { return false; }
}

export default function PublicPortal({ bookingRef: initialRef = '' }) {
  const { rentals, companySettings, openModal, modals } = useApp();
  const [ref, setRef]         = useState(initialRef);
  const [email, setEmail]     = useState('');
  const [error, setError]     = useState('');
  const [blocked, setBlocked] = useState(false);

  // If portal opens with no ref → show "Find your booking" form. If a ref is
  // in the URL, ref field is pre-filled but still editable in case of typo.
  const hasRefInUrl = !!initialRef;

  // After auth success we open ManageBookingModal. Listen to it to know if
  // user closed it → re-prompt for email (otherwise they're stuck on empty page).
  const modalOpen = !!modals['manage-booking'];
  const [authedRentalId, setAuthedRentalId] = useState(null);
  useEffect(() => {
    // Modal got closed externally → forget auth so customer must re-verify
    if (authedRentalId && !modalOpen) {
      setAuthedRentalId(null);
    }
  }, [modalOpen, authedRentalId]);

  // Try to find rental by booking ref (case-insensitive)
  const lookupRental = (refStr) => {
    const q = (refStr || '').trim().toUpperCase();
    if (!q) return null;
    return rentals.find(r => (r.bookingRef || '').toUpperCase() === q
                          || (r.id || '').toUpperCase() === q) || null;
  };

  function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (tooManyAttempts()) {
      setBlocked(true);
      setError('Too many attempts. Please wait 10 minutes and try again, or contact us directly.');
      return;
    }

    const r = lookupRental(ref);
    if (!r) {
      recordAttempt();
      setError(`No booking found with reference "${ref}". Check your confirmation email or contact us.`);
      return;
    }
    const emailMatch = (r.email || '').trim().toLowerCase() === email.trim().toLowerCase();
    if (!emailMatch) {
      const tries = recordAttempt();
      const remaining = MAX_TRIES_PER_WINDOW - tries;
      setError(remaining > 0
        ? `Email doesn't match the booking on file. ${remaining} attempt${remaining === 1 ? '' : 's'} remaining.`
        : 'Too many attempts. Please wait 10 minutes.');
      if (remaining <= 0) setBlocked(true);
      return;
    }
    // Success — open the manage modal
    setAuthedRentalId(r.id);
    openModal('manage-booking', r.id);
  }

  // ── Render ─────────────────────────────────────────────────────────
  const s = companySettings || {};
  const brand = s.brandColor || '#1A3A5C';

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f5f5f4',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Header */}
      <header style={{
        background: '#fff', borderBottom: '1px solid var(--bd)',
        padding: '14px 24px', display: 'flex', alignItems: 'center', gap: 14,
      }}>
        {s.logoDataUrl
          ? <img src={s.logoDataUrl} alt={s.name || 'Logo'} style={{ height: 36, maxWidth: 160, objectFit: 'contain' }} />
          : <div style={{ width: 36, height: 36, borderRadius: 8, background: brand, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14 }}>
              {(s.name || 'FR').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
            </div>}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1c1917' }}>{s.name || 'FleetRent'}</div>
          <div style={{ fontSize: 11, color: 'var(--tx3)' }}>Customer self-service portal</div>
        </div>
        {s.phone && (
          <a href={`tel:${s.phone}`} style={{
            display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600,
            color: brand, textDecoration: 'none', padding: '6px 12px',
            border: `1px solid ${brand}`, borderRadius: 20,
          }}>📞 {s.phone}</a>
        )}
      </header>

      {/* Main */}
      <main style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '40px 20px',
      }}>
        {!authedRentalId && (
          <div style={{
            background: '#fff', border: '1px solid var(--bd)', borderRadius: 14,
            padding: '28px 32px', boxShadow: '0 4px 16px rgba(0,0,0,.06)',
            width: '100%', maxWidth: 460,
          }}>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>📋</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#1c1917' }}>Manage your booking</div>
              <div style={{ fontSize: 12, color: 'var(--tx3)', marginTop: 6 }}>
                {hasRefInUrl
                  ? `Enter the email used for booking ${initialRef} to continue.`
                  : 'Enter your booking reference and the email used for the booking.'}
              </div>
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {!hasRefInUrl && (
                <div className="fg">
                  <label className="fl">Booking reference</label>
                  <input
                    type="text" className="fi" autoFocus
                    placeholder="e.g. BK-2563"
                    value={ref} onChange={e => setRef(e.target.value)}
                    autoCapitalize="characters"
                  />
                </div>
              )}
              <div className="fg">
                <label className="fl">Email used for booking</label>
                <input
                  type="email" className="fi" autoFocus={hasRefInUrl}
                  placeholder="your@email.com"
                  value={email} onChange={e => setEmail(e.target.value)}
                />
              </div>

              {error && (
                <div style={{
                  fontSize: 11, color: 'var(--ac)', background: 'var(--ac-l)',
                  border: '1px solid var(--ac-b)', borderRadius: 6, padding: '8px 10px',
                }}>
                  ⚠️ {error}
                </div>
              )}

              <button
                type="submit"
                disabled={blocked || !email || (!hasRefInUrl && !ref)}
                className="btn btn-primary"
                style={{
                  background: brand, borderColor: brand,
                  padding: '10px 16px', fontSize: 13, fontWeight: 600,
                  marginTop: 4, justifyContent: 'center',
                  opacity: (blocked || !email || (!hasRefInUrl && !ref)) ? 0.5 : 1,
                }}
              >
                Find my booking →
              </button>

              {hasRefInUrl && (
                <div style={{ fontSize: 10, color: 'var(--tx3)', textAlign: 'center', marginTop: 4 }}>
                  Booking reference: <strong style={{ fontFamily: "'DM Mono',monospace" }}>{initialRef}</strong>
                </div>
              )}
            </form>

            <div style={{
              marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--bd)',
              fontSize: 11, color: 'var(--tx3)', textAlign: 'center', lineHeight: 1.6,
            }}>
              Can't find your booking? Contact us:<br />
              {s.phone && <a href={`tel:${s.phone}`} style={{ color: brand, fontWeight: 600 }}>{s.phone}</a>}
              {s.phone && s.email && ' · '}
              {s.email && <a href={`mailto:${s.email}`} style={{ color: brand, fontWeight: 600 }}>{s.email}</a>}
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer style={{
        background: '#fff', borderTop: '1px solid var(--bd)',
        padding: '12px 24px', textAlign: 'center', fontSize: 10, color: 'var(--tx3)',
      }}>
        © {new Date().getFullYear()} {s.name || 'FleetRent'} · {s.address || ''}
        {s.vatNum && ` · VAT ${s.vatNum}`}
      </footer>

      {/* Modal mount — when authedRentalId set + openModal called, this renders the booking view */}
      <ManageBookingModal />
      <Toast />
    </div>
  );
}
